import { pageFaults, pageLabel, pageWarnings } from './pages'
import { calledFaults, calledWarnings } from './calledScreen'
import { counterFaults } from './counter'
import { KIOSK_SIGNAGE, mediaOffered, offersSignage } from '../lib/signage'
import { isIdlePlaylist, mediaFaults, mediaWarnings } from './media'
import { buttonWords, onCall, placeholderKeys } from './types'
import { DEFAULT_DATE_FORMAT, dateFormatShows, validDateFormat } from './dateFormat'
import { tickerLinesOf } from './screenBindings'
import type { Fault, LayoutDocument, LayoutElement, ElementKind, ElementOptions, LayoutKind } from './types'
import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'

/**
 * Editing a layout: the part with no pixels in it.
 *
 * Kept away from the canvas component so that the rules — what snapping does, what
 * an arrow key moves, what a new element looks like — can be read and changed
 * without reading a drag handler, and so the undo stack is a plain list of documents
 * rather than something entangled with React state.
 */

/** How far from an edge or a centre line the pointer has to be for it to bite. */
export const SNAP = 12

/** How far snapping reaches in design units at a scale: SNAP screen pixels, so it feels the same at any zoom. */
export const snapReach = (scale: number) => SNAP / scale
/** One press of an arrow key. Shift multiplies it. */
export const NUDGE = 1
export const NUDGE_FAR = 10

export interface Guide {
  axis: 'x' | 'y'
  at: number
}

/**
 * Snap a moving element to the canvas and to the elements already placed.
 *
 * Edges and centres, in that order of preference, and only one snap per axis: two
 * competing snaps make an element jitter between them, which feels like a bug in
 * the mouse rather than a feature of the editor.
 */
export function snap(
  moving: LayoutElement,
  others: LayoutElement[],
  canvas: { width: number; height: number },
  reach: number = SNAP,
): { x: number; y: number; guides: Guide[] } {
  const guides: Guide[] = []

  const xCandidates = [0, canvas.width / 2, canvas.width]
  const yCandidates = [0, canvas.height / 2, canvas.height]
  for (const el of others) {
    xCandidates.push(el.x, el.x + el.w / 2, el.x + el.w)
    yCandidates.push(el.y, el.y + el.h / 2, el.y + el.h)
  }

  let x = moving.x
  let y = moving.y

  // The three lines of the moving element that may snap: its leading edge, its
  // centre, and its trailing edge.
  const xEdges = [
    { offset: 0, value: moving.x },
    { offset: moving.w / 2, value: moving.x + moving.w / 2 },
    { offset: moving.w, value: moving.x + moving.w },
  ]
  const yEdges = [
    { offset: 0, value: moving.y },
    { offset: moving.h / 2, value: moving.y + moving.h / 2 },
    { offset: moving.h, value: moving.y + moving.h },
  ]

  let bestX: { distance: number; at: number; x: number } | null = null
  for (const edge of xEdges) {
    for (const candidate of xCandidates) {
      const distance = Math.abs(edge.value - candidate)
      if (distance <= reach && (!bestX || distance < bestX.distance)) {
        bestX = { distance, at: candidate, x: Math.round(candidate - edge.offset) }
      }
    }
  }
  if (bestX) {
    x = bestX.x
    guides.push({ axis: 'x', at: bestX.at })
  }

  let bestY: { distance: number; at: number; y: number } | null = null
  for (const edge of yEdges) {
    for (const candidate of yCandidates) {
      const distance = Math.abs(edge.value - candidate)
      if (distance <= reach && (!bestY || distance < bestY.distance)) {
        bestY = { distance, at: candidate, y: Math.round(candidate - edge.offset) }
      }
    }
  }
  if (bestY) {
    y = bestY.y
    guides.push({ axis: 'y', at: bestY.at })
  }

  return { x, y, guides }
}

/**
 * Keep an element inside the canvas.
 *
 * The server refuses a document with anything hanging off the edge, so the editor
 * makes it impossible rather than letting somebody design for ten minutes and then
 * be told no.
 */
export function clamp(el: LayoutElement, canvas: { width: number; height: number }): LayoutElement {
  const w = Math.max(8, Math.min(el.w, canvas.width))
  const h = Math.max(8, Math.min(el.h, canvas.height))
  return {
    ...el,
    w,
    h,
    x: Math.max(0, Math.min(Math.round(el.x), canvas.width - w)),
    y: Math.max(0, Math.min(Math.round(el.y), canvas.height - h)),
  }
}

/**
 * A box resized from a handle by (dx, dy), its moving edges stopped at the canvas
 * and at the smallest size, and the edges not being dragged left where they were.
 *
 * Resizing used to be clamp() after the fact, which keeps the size and moves the
 * box: the right edge dragged past the canvas pushed the whole element left, and
 * the left edge dragged past it moved the right one (found 2 October 2026).
 */
export function resizeWithin(
  box: { x: number; y: number; w: number; h: number },
  corner: string,
  dx: number,
  dy: number,
  canvas: { width: number; height: number },
): { x: number; y: number; w: number; h: number } {
  const MIN = 8
  const within = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)
  let left = box.x
  let top = box.y
  let right = box.x + box.w
  let bottom = box.y + box.h
  if (corner.includes('e')) right = within(right + dx, left + MIN, canvas.width)
  if (corner.includes('w')) left = within(left + dx, 0, right - MIN)
  if (corner.includes('s')) bottom = within(bottom + dy, top + MIN, canvas.height)
  if (corner.includes('n')) top = within(top + dy, 0, bottom - MIN)
  left = Math.round(left)
  top = Math.round(top)
  return { x: left, y: top, w: Math.round(right) - left, h: Math.round(bottom) - top }
}

/** How far, in screen pixels, the pointer travels before a press becomes a drag.
 *  A click to select must not nudge the box, record an undo step or save. */
export const DRAG_THRESHOLD = 3

export function pastThreshold(dx: number, dy: number, min: number = DRAG_THRESHOLD): boolean {
  return Math.hypot(dx, dy) >= min
}

/** Whether a gesture left an element's box where it found it. */
export function sameBox(a: LayoutElement | undefined, b: LayoutElement | undefined): boolean {
  if (!a || !b) return a === b
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h
}

/** A new picture's box shaped like its file, centred in the box it was given. */
export function fitPicture(el: LayoutElement, width: number, height: number, canvas: { width: number; height: number }): LayoutElement {
  if (!(width > 0) || !(height > 0)) return el
  const k = Math.min(el.w / width, el.h / height)
  const w = Math.max(8, Math.round(width * k))
  const h = Math.max(8, Math.round(height * k))
  return clamp({ ...el, x: el.x + Math.round((el.w - w) / 2), y: el.y + Math.round((el.h - h) / 2), w, h }, canvas)
}

export function replaceElement(doc: LayoutDocument, element: LayoutElement): LayoutDocument {
  return {
    ...doc,
    elements: doc.elements.map((el) => (el.id === element.id ? element : el)),
  }
}

/** What each kind is called in the palette and in the inspector, as phrase keys. */
export const ELEMENT_LABELS: Record<ElementKind, StaffKey> = {
  calls: 'designs.el.calls',
  queues: 'designs.el.queues',
  waitingTotal: 'designs.el.waitingTotal',
  clock: 'designs.el.clock',
  branchName: 'designs.el.branchName',
  languages: 'designs.el.languages',
  services: 'designs.el.services',
  ticketNumber: 'designs.el.ticketNumber',
  ticketFacts: 'designs.el.ticketFacts',
  ticketPosition: 'designs.el.ticketPosition',
  ticketWait: 'designs.el.ticketWait',
  qr: 'designs.el.qr',
  dateTime: 'designs.el.dateTime',
  validUntil: 'designs.el.validUntil',
  text: 'designs.el.text',
  image: 'designs.el.image',
  rule: 'designs.el.rule',
  media: 'designs.el.media',
  button: 'designs.el.button',
  ticker: 'designs.el.ticker',
  deskName: 'designs.el.deskName',
  serviceName: 'designs.el.serviceName',
  deskStatus: 'designs.el.deskStatus',
  deskNumber: 'designs.el.deskNumber',
  statusPill: 'designs.el.statusPill',
  clerkName: 'designs.el.clerkName',
  logo: 'designs.el.logo',
  panel: 'designs.el.panel',
}

/** What a kind is called, in the reader's language. */
export function elementLabel(kind: ElementKind, lang: StaffLanguage = 'en'): string {
  return t(ELEMENT_LABELS[kind], lang)
}

/**
 * What one element is called: its kind's name, except a board's two call
 * grids, which read the same list — the one starting further down is the
 * previous calls, the other the current call — and a kiosk's page and back buttons.
 */
export function elementName(el: LayoutElement, doc: LayoutDocument, lang: StaffLanguage = 'en'): string {
  if (el.kind === 'calls') {
    const skips = (o: LayoutElement) => (o.options?.skip ?? 0) > 0
    if (skips(el)) return t('designs.el.callsPrevious', lang)
    if (doc.elements.some((o) => o.kind === 'calls' && o.id !== el.id && skips(o))) return t('designs.el.callsCurrent', lang)
  }
  // A kiosk's page and back buttons, by what they do.
  if (el.kind === 'button' && el.action?.kind === 'page') return t('designs.el.pageButtonNamed', lang, { page: pageLabel(doc, el.action.pageId, lang) })
  if (el.kind === 'button' && el.action?.kind === 'back') return t('designs.ed.backButton', lang)
  if (el.kind === 'button' && el.action?.kind === 'booking') return t('designs.ed.bookingButton', lang)
  if (el.kind === 'dateTime') {
    const shows = dateFormatShows(el.options?.format)
    if (shows !== 'both') return t(shows === 'date' ? 'designs.el.date' : 'designs.el.time', lang)
  }
  return elementLabel(el.kind, lang)
}

/** A kind's name inside a sentence. */
const inSentence = (kind: ElementKind, lang: StaffLanguage) => elementLabel(kind, lang).toLowerCase()

/** Which kinds may be added to which layout, mirroring what the server allows. */
export const PALETTE: Record<LayoutKind, ElementKind[]> = {
  // No waitingTotal: retired 31 Oct; stored designs still draw it.
  // ticketNumber, serviceName and deskName: the Called screen's only (calledScreen.ts).
  // No deskNumber on any kind (owner, 1 Nov): the desk label's {number} covers it; stored designs still draw it.
  display: ['calls', 'queues', 'ticketNumber', 'serviceName', 'deskName', 'branchName', 'text', 'clock', 'dateTime', 'logo', 'media', 'image', 'ticker', 'rule', 'panel'],
  // No service grid: a kiosk offers service buttons (Add → Buttons). Old grids still render.
  kiosk: ['languages', 'ticketNumber', 'ticketFacts', 'branchName', 'qr', 'text', 'clock', 'dateTime', 'logo', 'media', 'image', 'rule', 'panel'],
  // Position and wait apart (the one block still draws in stored designs); dateTime is offered as Date and Time.
  ticket: ['ticketNumber', 'serviceName', 'ticketPosition', 'ticketWait', 'branchName', 'dateTime', 'validUntil', 'qr', 'text', 'logo', 'image', 'rule', 'panel'],
  // No media: signage plays on the hall TV only (owner, 8 Oct).
  counter: ['ticketNumber', 'serviceName', 'deskName', 'deskStatus', 'statusPill', 'clerkName', 'branchName', 'text', 'clock', 'dateTime', 'logo', 'image', 'rule', 'panel'],
}

/** The palette for a design, leaving out a playlist where signage was not bought: a
 *  button that can only be refused at Publish is worse than no button. A ticker has its own lines. */
export function paletteFor(
  kind: LayoutKind,
  modules: readonly string[] | undefined,
  kioskSignage: boolean = KIOSK_SIGNAGE,
): ElementKind[] {
  return PALETTE[kind].filter((k) => k !== 'media' || (offersSignage(modules) && mediaOffered(kind, kioskSignage)))
}

/** The kinds a layout cannot be published without, for the warning in the editor. */
export const REQUIRED: Record<LayoutKind, ElementKind[]> = {
  display: ['calls'],
  // A kiosk's way to a ticket — a service button, or an old grid — is pageProblems'.
  kiosk: ['ticketNumber'],
  ticket: ['ticketNumber'],
  counter: ['ticketNumber'],
}

/** Kinds that may appear only once.
 *
 *  Calls is deliberately not here. A board that shows the number being called at
 *  the size of a door, with the ones before it in a strip underneath, is two call
 *  elements reading the same list through different Skip and grid settings — and
 *  that is one of the designs we ship. The server agrees; see Document.Validate. */
export const SINGLETONS: ElementKind[] = ['ticketNumber']

/** Kinds added through the picture dialog: placed once a file is chosen, Cancel adds nothing. */
export const PICKED_FIRST: readonly ElementKind[] = ['image', 'logo']

/**
 * A new element, sized and placed so that it lands somewhere sensible.
 *
 * Dropped in the middle of the canvas rather than at the origin: an element added
 * at 0,0 on a 1920-wide board is behind whatever is already in the corner, and the
 * person who added it thinks nothing happened.
 */
export function newElement(kind: ElementKind, doc: LayoutDocument, options?: ElementOptions): LayoutElement {
  const size = defaultSize(kind, doc, options)
  const el: LayoutElement = {
    id: `${kind}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    x: Math.round((doc.canvas.width - size.w) / 2),
    y: Math.round((doc.canvas.height - size.h) / 2),
    w: size.w,
    h: size.h,
    // Above everything already there, so it is visible the moment it appears.
    z: Math.max(0, ...doc.elements.map((e) => e.z ?? 0)) + 10,
    style: { align: 'center' },
  }
  if (options) el.options = { ...options }
  if (kind === 'text') {
    el.content = { az: t('designs.el.text', 'az'), ru: t('designs.el.text', 'ru'), en: t('designs.el.text', 'en') }
  }
  if (kind === 'media') el.media = { items: [], source: 'playlist' }
  // A language switch stands in the top right corner.
  if (kind === 'languages') {
    el.x = doc.canvas.width - size.w - Math.round(doc.canvas.width * 0.05)
    el.y = Math.round(doc.canvas.height * 0.03)
    el.style = { align: 'right' }
  }
  // A hairline on paper; the box stays big enough to grab.
  if (kind === 'rule' && doc.kind === 'ticket') el.options = { ...el.options, thickness: 2 }
  if (kind === 'panel') {
    // Behind everything already there: a card is what other elements sit on.
    el.z = Math.min(0, ...doc.elements.map((e) => e.z ?? 0)) - 10
    el.style = doc.kind === 'ticket' ? { border: '#000000', radius: 12 } : { background: '--bg-surface', radius: 24, shadow: true }
  }
  return el
}

/** Average glyph width in em, to size a new box to its words. */
const GLYPH = 0.56
/** Digits and the hyphen of a ticket number run wider. */
const DIGIT = 0.62

/** How many characters a live text kind's words run to, as drawn. */
const SAMPLE_CHARS: Partial<Record<ElementKind, number>> = {
  text: 12,
  branchName: 16,
  serviceName: 16,
  deskName: 10,
  deskStatus: 10,
  clerkName: 14,
  clock: 5,
  ticketPosition: 15,
  ticketWait: 23,
  validUntil: 18,
  waitingTotal: 12,
}

/** The share of its box's height text is drawn at, as render.tsx does it. */
function textShare(kind: ElementKind, paper: boolean): number {
  if (paper) return 0.62
  if (kind === 'text') return 0.5
  if (kind === 'clock') return 0.7
  if (kind === 'clerkName') return 0.62
  return 0.6
}

/**
 * A new element's box: text-like kinds as wide as their words at the default size,
 * pictures and codes a modest box, lists a column. Screens scale by their shorter
 * side against 1080, so a small counter panel gets small boxes; paper is fixed.
 */
function defaultSize(kind: ElementKind, doc: LayoutDocument, options?: ElementOptions): { w: number; h: number } {
  const { width: W, height: H } = doc.canvas
  const paper = doc.kind === 'ticket'
  const side = Math.min(W, H)
  const unit = side / 1080
  const line = paper ? 44 : Math.max(40, Math.round(72 * unit))
  const max = Math.round(W * 0.9)
  // Words of n characters in a box h high, with room either side.
  const fit = (chars: number, h: number, share: number, em = GLYPH, pad = 1) => ({
    w: Math.min(max, Math.max(2 * h, Math.ceil(((chars * em + pad) * h * share) / 8) * 8)),
    h,
  })
  switch (kind) {
    case 'calls':
      return W >= H ? { w: Math.round(W * 0.5), h: Math.round(H * 0.6) } : { w: Math.round(W * 0.8), h: Math.round(H * 0.4) }
    case 'services':
      return { w: Math.round(W * 0.85), h: Math.round(H * 0.6) }
    case 'languages':
      return { w: Math.round(side * 0.24), h: Math.round(side * 0.06) }
    case 'ticketNumber':
      return fit(5, Math.round(H * 0.2), 0.78, DIGIT, 0.8)
    case 'deskNumber':
      return fit(2, Math.round(H * 0.25), 0.86, DIGIT, 0.6)
    case 'ticketFacts':
      return fit(23, Math.round(line * 3.4), 0.95 / (3 * 1.15 + 2 * 0.3))
    case 'statusPill':
      return fit(12, Math.round(line * 1.4), 0.42, GLYPH, 2.5)
    case 'clerkName':
      // The person glyph before the name.
      return fit(14, line, textShare(kind, paper), GLYPH, 2.2)
    case 'queues':
      // "Cash: 4  Cards: 1  Lending: 2": three rows with their gaps.
      return fit(30, Math.max(48, Math.round(80 * unit)), 0.42, GLYPH, 3.2)
    case 'dateTime':
      return fit((options?.format || DEFAULT_DATE_FORMAT).length, line, paper ? 0.62 : 0.6, DIGIT)
    case 'qr':
      return paper ? { w: 160, h: 160 } : { w: Math.round(side * 0.15), h: Math.round(side * 0.15) }
    case 'rule':
      return { w: Math.round(W * 0.5), h: 12 }
    case 'image':
    case 'logo': {
      const w = Math.round(side * 0.3)
      return { w, h: Math.round(w * (kind === 'logo' ? 0.3 : 0.5)) }
    }
    case 'panel':
      return { w: Math.round(W * 0.4), h: Math.round(H * 0.25) }
    case 'media':
      return { w: Math.round(W * 0.4), h: Math.round(H * 0.45) }
    case 'ticker':
      // A crawl reads across: the width is its point.
      return { w: max, h: Math.max(40, Math.round(64 * unit)) }
    default:
      return fit(SAMPLE_CHARS[kind] ?? 12, line, textShare(kind, paper))
  }
}

/**
 * What is wrong with this document, in the words the editor shows.
 *
 * A second implementation of the server's validation, which is a cost worth naming:
 * the two can drift, and the server is the authority. It exists so that the
 * "publish" button can be disabled with a reason attached, rather than a publish
 * being sent and refused — which is a worse experience for exactly the person least
 * able to interpret the refusal.
 */
export function problems(doc: LayoutDocument, lang: StaffLanguage = 'en'): string[] {
  return faults(doc, lang).map((f) => f.text)
}

/** problems(), each with the elements it is about, so the editor can point at them. */
export function faults(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  const found: Fault[] = []
  const counts = new Map<ElementKind, number>()
  const of = (el: LayoutElement) => (text: string): Fault => ({ text, ids: [el.id] })
  const idsOf = (kind: ElementKind) => doc.elements.filter((el) => el.kind === kind).map((el) => el.id)

  for (const el of doc.elements) {
    counts.set(el.kind, (counts.get(el.kind) ?? 0) + 1)
    if (el.x < 0 || el.y < 0 || el.x + el.w > doc.canvas.width || el.y + el.h > doc.canvas.height) {
      found.push(of(el)(t('designs.fault.offEdge', lang, { label: elementLabel(el.kind, lang) })))
    }
    if (el.kind === 'text' && !Object.values(el.content ?? {}).some((v) => v.trim())) {
      found.push(of(el)(t('designs.fault.textEmpty', lang)))
    }
    if (el.kind === 'image' && !el.assetId) {
      found.push(of(el)(t('designs.fault.noPicture', lang)))
    }
    found.push(...tickerProblems(el, lang).map(of(el)))
    found.push(...choiceProblems(el, lang).map(of(el)))
    found.push(...qrProblems(el, lang).map(of(el)))
    found.push(...panelProblems(el, doc.kind, lang).map(of(el)))
    if (el.kind === 'dateTime' && !validDateFormat(el.options?.format, doc.kind !== 'ticket')) {
      found.push(of(el)(t('designs.fault.dateFormat', lang, { format: el.options?.format ?? '' })))
    }
  }

  for (const kind of REQUIRED[doc.kind]) {
    if (!counts.get(kind)) {
      found.push({ text: t('designs.fault.required', lang, { label: elementLabel(kind, lang) }), ids: [] })
    } else if (!doc.elements.some((el) => el.kind === kind && !el.hidden)) {
      // Hidden is missing on the wall; the server refuses it at Publish.
      found.push({ text: t('designs.fault.requiredHidden', lang, { label: elementLabel(kind, lang) }), ids: idsOf(kind) })
    }
  }
  for (const kind of SINGLETONS) {
    if ((counts.get(kind) ?? 0) > 1) {
      found.push({ text: t('designs.fault.twice', lang, { label: inSentence(kind, lang) }), ids: idsOf(kind) })
    }
  }
  // A service grid is one per page, not one per design (pages.ts).
  if (doc.kind !== 'kiosk' && (counts.get('services') ?? 0) > 1) found.push({ text: t('designs.fault.twoGrids', lang), ids: idsOf('services') })
  found.push(...pageFaults(doc, lang))
  found.push(...counterFaults(doc, lang))
  found.push(...calledFaults(doc, lang))
  found.push(...mediaFaults(doc, lang))
  return found
}

/** A ticker's own lines; mirror layout.MaxTickerLines and layout.MaxTickerLineLength. */
export const MAX_TICKER_LINES = 10
export const MAX_TICKER_LINE = 200

/** Mirrors Element.tickerProblems (internal/layout/ticker.go), once per kind of fault. */
function tickerProblems(el: LayoutElement, lang: StaffLanguage): string[] {
  if (el.kind !== 'ticker') return []
  const lines = Object.values(el.content ?? {}).map(tickerLinesOf)
  const found: string[] = []
  if (lines.some((ls) => ls.length > MAX_TICKER_LINES)) found.push(t('designs.fault.tickerLines', lang, { max: MAX_TICKER_LINES }))
  const all = lines.flat()
  if (all.some((line) => [...line].length > MAX_TICKER_LINE)) found.push(t('designs.fault.tickerLong', lang, { max: MAX_TICKER_LINE }))
  if (all.some((line) => /[<>\u0000-\u001f\u007f-\u009f]/.test(line))) found.push(t('designs.fault.tickerPlain', lang))
  return found
}

/** How many services one grid may name. Mirrors layout.MaxServiceChoices. */
export const MAX_SERVICE_CHOICES = 60

/** What the server refuses about a grid's chosen services, in the editor's words. */
function choiceProblems(el: LayoutElement, lang: StaffLanguage): string[] {
  const ids = el.options?.serviceIds
  if (ids === undefined) return []
  if (el.kind !== 'services') {
    return [t('designs.fault.choiceNotGrid', lang, { label: inSentence(el.kind, lang) })]
  }
  const found: string[] = []
  if (ids.length > MAX_SERVICE_CHOICES) {
    found.push(t('designs.fault.tooManyChoices', lang, { max: MAX_SERVICE_CHOICES, chosen: ids.length }))
  }
  if (ids.some((id) => typeof id !== 'string' || !id.trim())) {
    found.push(t('designs.fault.choiceNoId', lang))
  }
  if (new Set(ids).size !== ids.length) {
    found.push(t('designs.fault.choiceTwice', lang))
  }
  return found
}

/** How many characters a QR code's website address may hold, counted in bytes —
 *  the server's len(string) counts UTF-8 bytes, and a byte limit checked against
 *  JavaScript's UTF-16 .length would let through what the server refuses.
 *  Mirrors layout.MaxQRURL. */
export const MAX_QR_URL = 100

/** Mirrors Element.panelProblems (internal/layout/panel.go): a border's width, and a ticket panel is an outline only. */
export function panelProblems(el: LayoutElement, kind: LayoutKind, lang: StaffLanguage): string[] {
  const found: string[] = []
  const width = el.style?.borderWidth
  if (width !== undefined && (width < 0 || width > MAX_BORDER_WIDTH)) found.push(t('designs.fault.borderWidth', lang, { max: MAX_BORDER_WIDTH }))
  if (el.kind !== 'panel' || kind !== 'ticket') return found
  if (el.style?.background) found.push(t('designs.fault.panelFill', lang))
  if (el.style?.shadow) found.push(t('designs.fault.panelShadow', lang))
  if (!el.style?.border) found.push(t('designs.fault.panelBorder', lang))
  return found
}

/** Mirrors layout.MaxBorderWidth. */
export const MAX_BORDER_WIDTH = 16

/**
 * What the server refuses about a QR code's choice, in the editor's words.
 *
 * Mirrors Element.qrProblems (internal/layout/layout.go) exactly: a format
 * nothing draws, a website code with no address or one a phone cannot open, and
 * an address on anything that is not a website code. A design's draft autosaves
 * on every edit (Layouts.tsx), straight into the server's own validation — so a
 * half-typed address that this function does not also catch is refused with the
 * server's raw English sentence rather than the reader's own language.
 */
function qrProblems(el: LayoutElement, lang: StaffLanguage): string[] {
  if (el.kind !== 'qr') {
    if (el.options?.url) {
      return [t('designs.fault.urlNotQr', lang, { label: inSentence(el.kind, lang) })]
    }
    return []
  }
  const format = el.options?.format
  // '' is the follow link on the server too (`case "", QRFollow`).
  if (format === undefined || format === '' || format === 'follow') {
    if (el.options?.url) {
      return [t('designs.fault.urlForFollow', lang)]
    }
    return []
  }
  if (format === 'website') {
    const url = el.options?.url ?? ''
    if (url.trim() === '') return [t('designs.fault.urlMissing', lang)]
    if (new TextEncoder().encode(url).length > MAX_QR_URL) {
      return [t('designs.fault.urlLong', lang, { max: MAX_QR_URL })]
    }
    if (!isWebAddress(url)) return [t('designs.fault.urlBad', lang)]
    return []
  }
  return [t('designs.fault.qrFormat', lang, { format: String(format) })]
}

/** Whether a string is an address a phone's browser could open. Mirrors the
 *  server's url.Parse check: scheme http or https in any case, and a host.
 *  The raw text must start with the scheme and its two slashes, because the
 *  browser's URL forgives what Go's does not — `https:example.com` and a
 *  leading space parse here and name no host there — and a space in the host
 *  is refused, which the browser's URL would trim or encode away. */
function isWebAddress(raw: string): boolean {
  const prefix = /^https?:\/\//i.exec(raw)
  if (!prefix) return false
  const authority = raw.slice(prefix[0].length).split(/[/?#]/)[0] ?? ''
  const host = authority.slice(authority.lastIndexOf('@') + 1)
  if (host === '' || /\s/.test(host)) return false
  try {
    return new URL(raw).hostname !== ''
  } catch {
    return false
  }
}

/**
 * What choosing a QR code's "Opens" setting does to its options: a code set to
 * open a website keeps nothing but that; a code set back to the follow link
 * drops the address with it, so a half-typed one left over from "Website" can
 * never reappear once "Place in line" is chosen again.
 *
 * Pulled out of the inspector so the one rule — switching away from Website
 * clears the address — is a function this file's own tests can call, rather
 * than something only a rendered <Select>'s onChange can be made to exercise.
 */
export function qrFormatPatch(value: 'follow' | 'website'): Pick<ElementOptions, 'format' | 'url'> {
  return value === 'website' ? { format: 'website' } : { format: undefined, url: undefined }
}

/**
 * What is worth saying about a design without refusing to publish it.
 *
 * A second list, deliberately. problems() is what a design cannot be published
 * with — an element off the canvas, a picture with no picture, a missing number
 * — and every one of them is something that is certainly wrong. Overlap is here.
 * What is here is something that may be: a slip that will be fine for most visits
 * and wrong for the long ones.
 *
 * The distinction is not a nicety. Every ticket design already published names its
 * placeholders and limits nothing, so a fault would take a design that is on the
 * printer this morning and make it unpublishable this afternoon, for a property
 * that did not exist yesterday. An editor that refuses to publish what it has
 * already published is an editor nobody believes the next time it refuses
 * something.
 *
 * What makes this worth more than the line of text it is: the canvas beside it now
 * shrinks and crops the sample the same way the paper will. The warning names the
 * design; the preview shows it.
 */
export function warnings(doc: LayoutDocument, lang: StaffLanguage = 'en'): string[] {
  return warningFaults(doc, lang).map((f) => f.text)
}

/** warnings(), each with the elements it is about (none for a page-level one). */
export function warningFaults(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  // Overlap is said, never refused: the designer may mean it (owner, 1 Nov).
  const covered = [...overlaps(doc, lang), ...mediaWarnings(doc, lang)]
  if (doc.kind === 'kiosk') return [...pageWarnings(doc, lang).map((text) => ({ text, ids: [] })), ...bareButtons(doc, lang), ...covered]
  if (doc.kind === 'display') return [...calledWarnings(doc, lang), ...covered]
  if (doc.kind !== 'ticket') return covered
  const found: Fault[] = [...covered]

  for (const el of doc.elements) {
    if (el.kind !== 'text' || el.options?.maxRows) continue
    const keys = Object.values(el.content ?? {}).flatMap(placeholderKeys)
    if (keys.length === 0) continue
    found.push({ text: t('designs.warn.rows', lang, { placeholder: `{{${keys[0]}}}` }), ids: [el.id] })
  }
  found.push(...paperCannotDo(doc, lang))
  return found
}

/** A button whose title is cleared in some language, with no second line there and no picture: blank, still pressable. */
function bareButtons(doc: LayoutDocument, lang: StaffLanguage): Fault[] {
  return doc.elements
    .filter(
      (el) =>
        el.kind === 'button' &&
        !el.hidden &&
        !el.assetId &&
        Object.keys(el.content ?? {}).some((code) => buttonWords(el.content, code) === '' && !buttonWords(el.subtitle, code)),
    )
    .map((el) => ({ text: t('designs.warn.bareButton', lang), ids: [el.id] }))
}

/**
 * What a thermal printer cannot do, said before somebody publishes it.
 *
 * Said rather than refused: the check is against 58mm paper, and every
 * installation we have runs 80mm.
 */
function paperCannotDo(doc: LayoutDocument, lang: StaffLanguage): Fault[] {
  if (doc.kind !== 'ticket') return []
  const found: Fault[] = []
  // 58mm paper is two thirds of this canvas. Anything under about eight dots tall
  // once it has been scaled is a grey smudge rather than a letter.
  const NARROW = 384 / 576
  for (const el of doc.elements) {
    if (el.kind === 'rule' || el.kind === 'image' || el.kind === 'qr' || el.kind === 'panel') continue
    if (el.h * NARROW < 12) {
      found.push({ text: t('designs.warn.short58', lang, { label: elementLabel(el.kind, lang) }), ids: [el.id] })
    }
  }
  return found
}

/**
 * Two things drawn on top of each other: a warning in the header, never a block
 * (owner, 1 Nov). Pictures, rules and panels are laid over or under on purpose; a
 * sliver (under a fifth of the smaller box) is how anybody aligns by hand.
 */
const BACKDROPS = new Set<ElementKind>(['image', 'rule', 'panel'])

/** Pictures, lines and panels: laid over or under other elements on purpose. */
export const isBackdrop = (kind: ElementKind) => BACKDROPS.has(kind)
const BURIED = 0.2

function overlaps(doc: LayoutDocument, lang: StaffLanguage): Fault[] {
  const found: Fault[] = []
  // A kiosk's idle playlist covers the welcome screen on purpose.
  const solid = doc.elements.filter((el) => !el.hidden && !BACKDROPS.has(el.kind) && !(doc.kind === 'kiosk' && isIdlePlaylist(el)))

  for (let i = 0; i < solid.length; i++) {
    for (let j = i + 1; j < solid.length; j++) {
      const a = solid[i]!
      const b = solid[j]!
      // Only where both are drawn at once. A kiosk's welcome screen and its
      // choosing screen share a canvas and are never on it together.
      if (!sharesAScreen(a, b)) continue
      if (playlistOnCalls(a, b) || playlistOnCalls(b, a)) continue

      const width = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)
      const height = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y)
      if (width <= 0 || height <= 0) continue

      const smaller = Math.min(a.w * a.h, b.w * b.h)
      if (smaller <= 0 || (width * height) / smaller < BURIED) continue

      found.push({
        text:
          elementName(a, doc, lang) === elementName(b, doc, lang)
            ? t('designs.fault.overlapSame', lang, { label: elementName(a, doc, lang) })
            : t('designs.fault.overlap', lang, { label: elementName(a, doc, lang), other: elementName(b, doc, lang) }),
        ids: [a.id, b.id],
      })
    }
  }
  return found
}

/**
 * A new element moved to the free spot nearest the middle of the canvas, or left
 * where it is when there is none.
 *
 * Everything used to land in the middle, and on a board the middle is the called
 * numbers: every element added was "on top of each other" at once, Publish was
 * refused until it was dragged away, and the new box sat over the grid where a
 * click meant for the grid took it instead (found 2 October 2026). Pictures and
 * rules are left in the middle: laying them over something is ordinary design.
 */
export function findRoom(el: LayoutElement, doc: LayoutDocument): LayoutElement {
  if (BACKDROPS.has(el.kind)) return el
  const { width: W, height: H } = doc.canvas
  if (el.w > W || el.h > H) return el
  const others = doc.elements.filter((o) => o.id !== el.id && !BACKDROPS.has(o.kind) && sharesAScreen(el, o))
  const free = (x: number, y: number) =>
    others.every((o) => x >= o.x + o.w || o.x >= x + el.w || y >= o.y + o.h || o.y >= y + el.h)
  if (free(el.x, el.y)) return el
  const step = Math.max(4, Math.round(Math.min(W, H) / 60))
  const cx = (W - el.w) / 2
  const cy = (H - el.h) / 2
  let best: { x: number; y: number; d: number } | null = null
  const xs = new Set<number>([Math.round(cx), W - el.w])
  for (let x = 0; x <= W - el.w; x += step) xs.add(x)
  const ys = new Set<number>([Math.round(cy), H - el.h])
  for (let y = 0; y <= H - el.h; y += step) ys.add(y)
  // Also flush against each neighbour, so a slot exactly the height left over is found.
  for (const o of others) {
    if (o.x + o.w + el.w <= W) xs.add(o.x + o.w)
    if (o.x - el.w >= 0) xs.add(o.x - el.w)
    if (o.y + o.h + el.h <= H) ys.add(o.y + o.h)
    if (o.y - el.h >= 0) ys.add(o.y - el.h)
  }
  for (const y of ys) {
    for (const x of xs) {
      if (!free(x, y)) continue
      const d = (x - cx) ** 2 + (y - cy) ** 2
      if (!best || d < best.d) best = { x, y, d }
    }
  }
  return best ? { ...el, x: best.x, y: best.y } : el
}

const playlistOnCalls = (a: LayoutElement, b: LayoutElement) => a.kind === 'media' && b.kind === 'calls'

/** Whether two elements are ever drawn at the same moment. */
function sharesAScreen(a: LayoutElement, b: LayoutElement): boolean {
  // A display's Called screen and its board are never drawn together.
  if (onCall(a) !== onCall(b)) return false
  const on = (el: LayoutElement) => el.screens ?? []
  const left = on(a)
  const right = on(b)
  // An element with no list is on every screen, so it meets everything.
  const screens = left.length === 0 || right.length === 0 || left.some((s) => right.includes(s))
  if (!screens) return false
  // On the choosing screen, two elements on different menu pages never meet.
  const pa = a.pages ?? []
  const pb = b.pages ?? []
  if (pa.length === 0 || pb.length === 0) return true
  return pa.some((p) => pb.includes(p))
}

/**
 * Whether two documents say the same thing.
 *
 * Compared structurally rather than by serialising both to JSON, because the two
 * sides are made differently: one came off the wire in the order Go's marshaller
 * writes a struct, the other has been through a browser that rebuilds objects as
 * it edits them. Two documents that differ only in the order their keys happen to
 * sit in are the same document, and a string comparison says they are not — which
 * would light "edited since" on a design nobody has touched, and leave Publish
 * live with nothing to publish.
 *
 * Arrays are compared in order, deliberately: the order of the elements is the
 * stacking order, and a design with the same shapes stacked differently is a
 * different design.
 */
export function sameDocument(a: unknown, b: unknown): boolean {
  if (a === b) return true
  // null is typeof "object", and an absent optional arrives as undefined. Neither
  // is worth a deep walk, and treating them as equal to each other is what makes
  // an element that dropped an optional property compare equal to one that never
  // had it.
  if (a == null || b == null) return a == null && b == null
  if (typeof a !== 'object' || typeof b !== 'object') return false

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false
    return a.every((item, i) => sameDocument(item, b[i]))
  }

  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  // Undefined-valued keys are skipped on both sides: `{ note: undefined }` and `{}`
  // serialise identically and mean the same thing, and counting keys without this
  // makes them differ.
  const keys = (o: Record<string, unknown>) => Object.keys(o).filter((k) => o[k] !== undefined)
  const leftKeys = keys(left)
  const rightKeys = keys(right)
  if (leftKeys.length !== rightKeys.length) return false
  return leftKeys.every(
    (k) => Object.prototype.hasOwnProperty.call(right, k) && sameDocument(left[k], right[k]),
  )
}
